/**
 * WorkshopSummaryCard (BON-145) — the workshop tile at the top of the booking
 * detail view. Desktop renders the workshop details inline under the name with
 * the seats badge right-aligned, plus the Customer/Source/History band (matches
 * the desktop comp); tablet/phone stack the details under a divider with the
 * seats badge beneath the name (the separate CustomerCard carries the customer
 * info there). Pure (data in → markup out).
 */
import { CalendarDays, MapPin, Sprout, Users } from 'lucide-react';
import type { ReactNode } from 'react';

import type { BookingDetail } from '../../api/bookingsV2';
import { getWorkshopTypeColors, getWorkshopTypeIcon } from '../../utils/workshopTypeIcon';

import { cardCls, fmtCreatedDate, fmtWorkshopWhen, InfoLine, META } from './bookingCardShared';
import { historyLabel } from './bookingDetailModel';

/** Desktop Customer / Source / History strip, shown as the lower band of the
 *  workshop card (matches the desktop comp). Phone/tablet use CustomerCard. */
function CustomerBand({ booking }: { booking: BookingDetail }) {
  const { customer, source } = booking;
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
  const history = historyLabel(customer.priorBookingsCount);
  return (
    <div className="mt-5 pt-5 border-t border-stone-200 dark:border-stone-700 grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div>
        <span className={META}>Customer</span>
        <p className="mt-1 text-sm font-medium text-bark-900 dark:text-stone-100">
          {name || customer.email || 'Unknown'}
        </p>
        {customer.email && (
          <p className="text-xs text-stone-500 dark:text-stone-400 truncate">{customer.email}</p>
        )}
      </div>
      <div>
        <span className={META}>Source</span>
        <p className="mt-1 text-sm font-medium text-bark-900 dark:text-stone-100">
          {source === 'nebari' ? 'Online' : 'Migrated'}
        </p>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          Created {fmtCreatedDate(booking.createdAt)}
        </p>
      </div>
      <div>
        <span className={META}>History</span>
        <p className="mt-1 text-sm font-medium text-bark-900 dark:text-stone-100">
          {history.count}
        </p>
        <p className="text-xs text-stone-500 dark:text-stone-400">{history.note}</p>
      </div>
    </div>
  );
}

/** Tree-type icon tile: the species' icon art when present, else a solid
 *  brand-colored tile with a generic sprout glyph. Mirrors the booking app. */
function WorkshopTypeIcon({ name }: { name: string | null }) {
  const icon = getWorkshopTypeIcon(name);
  const colors = getWorkshopTypeColors(name);
  return (
    <span
      className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {icon ? (
        <img src={icon} alt="" className="w-full h-full object-cover" />
      ) : (
        <Sprout size={26} />
      )}
    </span>
  );
}

function workshopDetailItems(
  workshop: BookingDetail['workshop'],
): { icon: ReactNode; text: ReactNode }[] {
  const items: { icon: ReactNode; text: ReactNode }[] = [
    {
      icon: <MapPin size={15} />,
      text: (
        <>
          {workshop.venue.name} &middot; {workshop.venue.city}, {workshop.venue.state}
        </>
      ),
    },
    { icon: <CalendarDays size={15} />, text: fmtWorkshopWhen(workshop) },
  ];
  if (workshop.teacherName) {
    items.push({ icon: <Users size={15} />, text: <>Taught by {workshop.teacherName}</> });
  }
  return items;
}

/** Desktop header: name + inline details on the left, seats badge right-aligned. */
function DesktopHeader({
  workshop,
  seatsBadge,
}: {
  workshop: BookingDetail['workshop'];
  seatsBadge: ReactNode;
}) {
  return (
    <div className="flex items-start gap-4">
      <WorkshopTypeIcon name={workshop.treeSpeciesName} />
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-bold text-bark-900 dark:text-stone-100">
          {workshop.name ?? '—'}
        </h2>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-500 dark:text-stone-400">
          {workshopDetailItems(workshop).map((d, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              <span className="flex-shrink-0 text-stone-400">{d.icon}</span>
              {d.text}
            </span>
          ))}
        </div>
      </div>
      {seatsBadge}
    </div>
  );
}

/** Tablet/phone header: seats badge under the name, details stacked below a divider. */
function StackedHeader({
  workshop,
  seatsBadge,
}: {
  workshop: BookingDetail['workshop'];
  seatsBadge: ReactNode;
}) {
  return (
    <>
      <div className="flex items-start gap-4">
        <WorkshopTypeIcon name={workshop.treeSpeciesName} />
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-bark-900 dark:text-stone-100">
            {workshop.name ?? '—'}
          </h2>
          {seatsBadge && <div className="mt-1.5">{seatsBadge}</div>}
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-700 space-y-2">
        {workshopDetailItems(workshop).map((d, i) => (
          <InfoLine key={i} icon={d.icon}>
            {d.text}
          </InfoLine>
        ))}
      </div>
    </>
  );
}

export function WorkshopSummaryCard({
  booking,
  desktop = false,
}: {
  booking: BookingDetail;
  desktop?: boolean;
}) {
  const { workshop } = booking;
  const seatsBadge =
    workshop.seatsLeft !== null ? (
      <span className="flex-shrink-0 inline-block text-xs px-2.5 py-1 rounded-full bg-stone-100 dark:bg-stone-700/60 text-stone-600 dark:text-stone-300">
        {workshop.seatsLeft} seats left
      </span>
    ) : null;

  return (
    <div className={cardCls}>
      <div className="p-5">
        {desktop ? (
          <DesktopHeader workshop={workshop} seatsBadge={seatsBadge} />
        ) : (
          <StackedHeader workshop={workshop} seatsBadge={seatsBadge} />
        )}
        {desktop && <CustomerBand booking={booking} />}
      </div>
    </div>
  );
}
